import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    createServiceSubscription,
    fetchServices,
    fetchServicesDetails,
    fetchAvailableCustomers,
    updateServiceFrequency,
    createService,
} from './api';
import type {
    ServiceCustomerDetailsOutput,
    Service,
    ServiceCustomerInput,
    ServiceCustomerFrequencyInput,
    AvailableCustomer,
    DataRow,
    DataRowDetail,
    NewServiceEntry,
} from './types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ── Helpers de dominio ────────────────────────────────────────────────────────

export const statusColor = (status: string | null | undefined): 'success' | 'warning' | 'error' | 'primary' => {
    if (!status) return 'primary';
    const s = status.toUpperCase();
    if (s === 'OK') return 'success';
    if (s === 'ADVERTENCIA') return 'warning';
    if (s === 'ERROR') return 'error';
    return 'primary';
};

const getDefaultFrequency = (service: Service) =>
    service.defaultFrequency ?? service.defaultFrecuency ?? 0;

export const getSubscriptionDefaultFrequency = (service: Service) =>
    service.defaultFrequency ?? service.defaultFrecuency ?? 10;

const getDefaultResultId = (service: Service | undefined) => {
    if (!service) return null;
    const possibleResults = service.possibleResults ?? [];
    if (possibleResults.length === 0) return null;

    const sorted = [...possibleResults].sort(
        (a, b) => (a.possibleResult?.priority ?? 0) - (b.possibleResult?.priority ?? 0)
    );

    return sorted[0]?.id ?? null;
};

// ── Hook principal ────────────────────────────────────────────────────────────

export const useTracingCrud = () => {
    // ── Estado de selección master-detail ────────────────────────────────────────
    const [selectedClientId, setSelectedClientId] = useState<string | number | null>(null);

    // ── Estado de expansión de servicios ────────────────────────────────────────
    const [expandedClientId, setExpandedClientId] = useState<string | number | null>(null);

    // ── Estado de búsqueda de clientes ────────────────────────────────────────────
    const [customerSearch, setCustomerSearch] = useState('');

    // ── Estado de diálogo agregar servicio ───────────────────────────────────────
    const [isAddServiceOpen, setIsAddServiceOpen] = useState(false);
    const [addServiceSearch, setAddServiceSearch] = useState('');

    // ── Estado de edición de frecuencia ──────────────────────────────────────────
    const [frequencyOverrides, setFrequencyOverrides] = useState<Record<number, number>>({});
    const [isEditFrequencyOpen, setIsEditFrequencyOpen] = useState(false);
    const [editFrequencyValue, setEditFrequencyValue] = useState(0);
    const [editService, setEditService] = useState<DataRowDetail | null>(null);
    const [editFinishDate, setEditFinishDate] = useState<string>('');
    const [editInactive, setEditInactive] = useState(false);

    // ── Estado del diálogo unificado de suscripciones ───────────────────────────────
    const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
    const [subscriptionSelectedCustomers, setSubscriptionSelectedCustomers] = useState<Record<number, boolean>>({});
    const [subscriptionSelectedServices, setSubscriptionSelectedServices] = useState<Record<number, boolean>>({});
    const [subscriptionFrequencies, setSubscriptionFrequencies] = useState<Record<number | string, number>>({});
    const [subscriptionCustomerSearch, setSubscriptionCustomerSearch] = useState('');
    const [subscriptionServiceSearch, setSubscriptionServiceSearch] = useState('');
    const [subscriptionNewServices, setSubscriptionNewServices] = useState<NewServiceEntry[]>([]);

    // ── Fetch de datos ────────────────────────────────────────────────────────────
    const servicesDetailsQuery = useQuery<ServiceCustomerDetailsOutput[], Error>({
        queryKey: ['servicesDetails'],
        queryFn: fetchServicesDetails,
    });

    const servicesQuery = useQuery<Service[], Error>({
        queryKey: ['services'],
        queryFn: fetchServices,
    });

    const availableCustomersQuery = useQuery<AvailableCustomer[], Error>({
        queryKey: ['availableCustomers'],
        queryFn: fetchAvailableCustomers,
    });

    const queryClient = useQueryClient();

    const addServiceMutation = useMutation({
        mutationFn: createServiceSubscription,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['servicesDetails'] });
            queryClient.invalidateQueries({ queryKey: ['serviceStatuses'] });
            queryClient.invalidateQueries({ queryKey: ['availableCustomers'] });
        },
    });

    const updateFrequencyMutation = useMutation({
        mutationFn: updateServiceFrequency,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['servicesDetails'] });
            toast.success('Frecuencia guardada correctamente');
        },
        onError: () => {
            toast.error('No se pudo guardar la frecuencia');
        },
    });

    const createServiceMutation = useMutation({
        mutationFn: createService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['services'] });
        },
    });

    // ── Mapeo y ordenamiento de datos ────────────────────────────────────────────
    const tableData = useMemo<DataRow[]>(() => {
        const servicesDetails = servicesDetailsQuery.data ?? [];

        const mapped = servicesDetails.map(customerData => {
            const details: DataRowDetail[] = customerData.services.map(svc => ({
                id: svc.serviceCustomerId,
                serviceId: svc.serviceId,
                service: svc.name,
                status: svc.status,
                statusPriority: svc.statusPriority,
                lastRun: svc.lastExecution
                    ? new Date(svc.lastExecution).toLocaleString('es-AR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                    })
                    : '-',
                frequency: `${svc.frequency} min`,
                frequencyValue: svc.frequency,
                finishDateValue: svc.finishDate ?? null,
                inactive: svc.inactive,
            }));

            // El estado del cliente lo calcula el backend en /services/details: excluye
            // servicios inactivos, vencidos por fecha y sin resultado. Derivarlo acá dejaba
            // al cliente pegado a un estado viejo aunque sus servicios vigentes cambiaran.
            return {
                id: customerData.customerId,
                customerId: customerData.customerId,
                eiffelCustomerId: customerData.eiffelCustomerId,
                customer: customerData.customerName,
                business: customerData.businessName,
                status: customerData.status?.trim() || 'Indefinido',
                statusPriority: customerData.statusPriority,
                details,
            };
        });

        return mapped.sort((a, b) => {
            if (a.statusPriority !== b.statusPriority) {
                return a.statusPriority - b.statusPriority;
            }
            return a.customer.localeCompare(b.customer);
        });
    }, [servicesDetailsQuery.data]);

    // ── Filtrado de clientes por búsqueda ─────────────────────────────────────────
    const filteredTableData = useMemo<DataRow[]>(() => {
        const normalizedSearch = customerSearch.trim().toLowerCase();
        if (!normalizedSearch) return tableData;
        return tableData.filter(row =>
            row.customer.toLowerCase().includes(normalizedSearch) ||
            row.business.toLowerCase().includes(normalizedSearch)
        );
    }, [customerSearch, tableData]);

    // ── Garantizar cliente seleccionado visible ───────────────────────────────────
    useEffect(() => {
        if (tableData.length === 0) return;
        const currentIsVisible = tableData.some(c => c.id === selectedClientId);
        if (!selectedClientId || !currentIsVisible) {
            setSelectedClientId(tableData[0].id);
        }
    }, [tableData, selectedClientId]);

    const selectedClient = useMemo<DataRow | undefined>(
        () => tableData.find(client => client.id === selectedClientId),
        [tableData, selectedClientId]
    );

    const selectedClientRaw = useMemo<DataRow | undefined>(
        () => tableData.find(client => client.id === selectedClientId),
        [tableData, selectedClientId]
    );

    // ── Servicios disponibles para agregar ───────────────────────────────────────
    const availableServices = useMemo<Service[]>(() => {
        const selectedServiceIds = new Set((selectedClientRaw?.details ?? []).map(d => d.serviceId));
        return (servicesQuery.data ?? [])
            .filter(service => !service.inactive)
            .filter(service => !selectedServiceIds.has(service.id))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [servicesQuery.data, selectedClientRaw]);

    // ── Servicios disponibles filtrados por búsqueda ────────────────────────────
    const filteredAvailableServices = useMemo<Service[]>(() => {
        const search = addServiceSearch.trim().toLowerCase();
        if (!search) return availableServices;
        return availableServices.filter(s =>
            s.name.toLowerCase().includes(search) ||
            s.description?.toLowerCase().includes(search) ||
            s.id.toString().includes(search)
        );
    }, [availableServices, addServiceSearch]);

    // ── Derived: diálogo de suscripciones ───────────────────────────────────────
    const subscriptionSelectedCustomerCount = useMemo(
        () => Object.values(subscriptionSelectedCustomers).filter(Boolean).length,
        [subscriptionSelectedCustomers]
    );

    const subscriptionSelectedServiceCount = useMemo(
        () => Object.values(subscriptionSelectedServices).filter(Boolean).length,
        [subscriptionSelectedServices]
    );

    const subscriptionValidNewServicesCount = useMemo(
        () => subscriptionNewServices.filter(s => s.name.trim()).length,
        [subscriptionNewServices]
    );

    const subscriptionFilteredCustomers = useMemo(() => {
        const normalized = subscriptionCustomerSearch.trim().toLowerCase();
        return [...(availableCustomersQuery.data ?? [])]
            .filter(customer =>
                customer.name.toLowerCase().includes(normalized) ||
                String(customer.id).includes(normalized)
            )
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [availableCustomersQuery.data, subscriptionCustomerSearch]);

    const subscriptionActiveServices = useMemo(() => {
        return [...(servicesQuery.data ?? [])]
            .filter(service => !service.inactive)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [servicesQuery.data]);

    const subscriptionFilteredServices = useMemo(() => {
        const normalized = subscriptionServiceSearch.trim().toLowerCase();
        if (!normalized) return subscriptionActiveServices;
        return subscriptionActiveServices.filter(service =>
            service.name.toLowerCase().includes(normalized) ||
            service.description?.toLowerCase().includes(normalized) ||
            String(service.id).includes(normalized)
        );
    }, [subscriptionActiveServices, subscriptionServiceSearch]);

    const existingSubscriptionMap = useMemo(() => {
        const map = new Map<number, Set<number>>();
        for (const row of tableData) {
            const activeIds = new Set(row.details.filter(d => !d.inactive).map(d => d.serviceId));
            map.set(row.customerId, activeIds);
        }
        return map;
    }, [tableData]);

    const subscriptionPairsCounts = useMemo(() => {
        const selectedCustomerIds = Object.entries(subscriptionSelectedCustomers)
            .filter(([, v]) => v).map(([id]) => Number(id));
        const selectedServiceIds = Object.entries(subscriptionSelectedServices)
            .filter(([, v]) => v).map(([id]) => Number(id));

        let newCount = 0;
        let skippedCount = 0;

        for (const customerId of selectedCustomerIds) {
            const existing = existingSubscriptionMap.get(customerId) ?? new Set<number>();
            for (const serviceId of selectedServiceIds) {
                if (existing.has(serviceId)) skippedCount++;
                else newCount++;
            }
            newCount += subscriptionValidNewServicesCount;
        }

        return { newCount, skippedCount };
    }, [subscriptionSelectedCustomers, subscriptionSelectedServices, existingSubscriptionMap, subscriptionValidNewServicesCount]);

    // ── Inicializar frequencyOverrides al abrir diálogo ──────────────────────────
    useEffect(() => {
        if (!isAddServiceOpen) {
            setFrequencyOverrides({});
            return;
        }
        setFrequencyOverrides(prev => {
            const next = { ...prev };
            availableServices.forEach(service => {
                if (next[service.id] === undefined) {
                    next[service.id] = getDefaultFrequency(service);
                }
            });
            return next;
        });
    }, [isAddServiceOpen, availableServices]);

    // ── Handlers: selección y expansión ─────────────────────────────────────────
    const handleSelectClient = (id: string | number) => setSelectedClientId(id);

    const handleToggleExpand = (id: string | number) => {
        setExpandedClientId(prev => (prev === id ? null : id));
        setSelectedClientId(id);
    };

    // ── Handlers: agregar servicio ────────────────────────────────────────────────
    const handleOpenAddServiceForClient = (clientId: string | number) => {
        setSelectedClientId(clientId);
        setIsAddServiceOpen(true);
    };

    const handleCloseAddService = () => {
        setIsAddServiceOpen(false);
        setAddServiceSearch('');
    };

    const handleAddService = async (serviceId: number, frecuency: number) => {
        if (!selectedClientRaw) return;

        const service = servicesQuery.data?.find(s => s.id === serviceId);
        if (!service) return;

        const resultId = getDefaultResultId(service);
        if (!resultId) return;

        const safeFrequency = Number.isFinite(frecuency) ? frecuency : getDefaultFrequency(service);

        const payload: ServiceCustomerInput = {
            serviceId: service.id,
            customerId: selectedClientRaw.customerId,
            frecuency: safeFrequency,
            resultId,
            startDate: new Date().toISOString(),
            finishDate: null,
            inactive: false,
        };

        try {
            await addServiceMutation.mutateAsync(payload);
            toast.success('Servicio agregado correctamente');
        } catch {
            toast.error('No se pudo agregar el servicio');
        }
    };

    // ── Handlers: edición de frecuencia ────────────────────────────────────────────
    const handleFrequencyChange = (serviceId: number, value: string) => {
        const parsed = Number(value);
        setFrequencyOverrides(prev => ({
            ...prev,
            [serviceId]: Number.isNaN(parsed) ? 0 : parsed,
        }));
    };

    const handleOpenEditFrequency = (service: DataRowDetail) => {
        setEditService(service);
        setEditFrequencyValue(service.frequencyValue);
        setEditFinishDate(service.finishDateValue ? new Date(service.finishDateValue).toISOString().slice(0, 10) : '');
        setEditInactive(service.inactive);
        setIsEditFrequencyOpen(true);
    };

    const handleCloseEditFrequency = () => {
        setIsEditFrequencyOpen(false);
        setEditService(null);
    };

    const handleSaveFrequency = async () => {
        if (!editService) return;
        const safe = Number.isFinite(editFrequencyValue) ? editFrequencyValue : 0;
        if (safe <= 0) return;

        const payload: ServiceCustomerFrequencyInput = {
            serviceCustomerId: editService.id,
            frecuency: safe,
            finishDate: editFinishDate ? new Date(editFinishDate).toISOString() : null,
            inactive: editInactive,
        };

        await updateFrequencyMutation.mutateAsync(payload);
        handleCloseEditFrequency();
    };

    const handleInactiveChange = (checked: boolean) => {
        setEditInactive(checked);
        if (checked) {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            setEditFinishDate(`${year}-${month}-${day}`);
        } else {
            setEditFinishDate('');
        }
    };

    // ── Handlers: diálogo unificado de suscripciones (estado interno) ────────────
    const handleToggleSubscriptionCustomer = (customerId: number) => {
        setSubscriptionSelectedCustomers(prev => ({ ...prev, [customerId]: !prev[customerId] }));
    };

    const handleToggleAllSubscriptionCustomers = () => {
        const allIds = subscriptionFilteredCustomers.map(c => c.id);
        const allSelected = allIds.every(id => subscriptionSelectedCustomers[id]);

        if (allSelected) {
            setSubscriptionSelectedCustomers({});
        } else {
            const next: Record<number, boolean> = {};
            allIds.forEach(id => { next[id] = true; });
            setSubscriptionSelectedCustomers(next);
        }
    };

    const handleToggleSubscriptionService = (serviceId: number) => {
        setSubscriptionSelectedServices(prev => {
            const isSelected = prev[serviceId];
            if (isSelected) {
                setSubscriptionFrequencies(freqPrev => {
                    const next = { ...freqPrev };
                    delete next[serviceId];
                    return next;
                });
                return { ...prev, [serviceId]: false };
            }

            const service = (servicesQuery.data ?? []).find(s => s.id === serviceId);
            if (service) {
                setSubscriptionFrequencies(freqPrev => ({
                    ...freqPrev,
                    [serviceId]: getSubscriptionDefaultFrequency(service),
                }));
            }
            return { ...prev, [serviceId]: true };
        });
    };

    const handleToggleAllSubscriptionServices = () => {
        const allIds = subscriptionActiveServices.map(s => s.id);
        const allSelected = allIds.every(id => subscriptionSelectedServices[id]);

        if (allSelected) {
            setSubscriptionSelectedServices({});
            setSubscriptionFrequencies({});
        } else {
            const nextSelected: Record<number, boolean> = {};
            const nextFreqs: Record<number, number> = {};
            subscriptionActiveServices.forEach(s => {
                nextSelected[s.id] = true;
                nextFreqs[s.id] = getSubscriptionDefaultFrequency(s);
            });
            setSubscriptionSelectedServices(nextSelected);
            setSubscriptionFrequencies(nextFreqs);
        }
    };

    const handleSubscriptionFrequencyChange = (id: number | string, value: number) => {
        setSubscriptionFrequencies(prev => ({ ...prev, [id]: value }));
    };

    const handleAddSubscriptionNewService = () => {
        const entry: NewServiceEntry = {
            tempId: `temp-${Date.now()}`,
            name: '',
            description: '',
            frequency: 10,
        };
        setSubscriptionNewServices(prev => [...prev, entry]);
    };

    const handleRemoveSubscriptionNewService = (tempId: string) => {
        setSubscriptionNewServices(prev => prev.filter(s => s.tempId !== tempId));
        setSubscriptionFrequencies(prev => {
            const next = { ...prev };
            delete next[tempId];
            return next;
        });
    };

    const handleUpdateSubscriptionNewService = (
        tempId: string,
        field: keyof Omit<NewServiceEntry, 'tempId'>,
        value: string | number
    ) => {
        setSubscriptionNewServices(prev =>
            prev.map(s => (s.tempId === tempId ? { ...s, [field]: value } : s))
        );
        if (field === 'frequency') {
            setSubscriptionFrequencies(prev => ({ ...prev, [tempId]: value as number }));
        }
    };

    // ── Handlers: diálogo unificado de suscripciones ────────────────────────────────
    const handleOpenSubscriptionDialog = () => {
        setSubscriptionSelectedCustomers({});
        setSubscriptionSelectedServices({});
        setSubscriptionFrequencies({});
        setSubscriptionCustomerSearch('');
        setSubscriptionServiceSearch('');
        setSubscriptionNewServices([]);
        setIsSubscriptionDialogOpen(true);
    };

    const handleCloseSubscriptionDialog = () => setIsSubscriptionDialogOpen(false);

    const handleConfirmSubscriptions = async () => { try {
        const selectedCustomerIds = Object.entries(subscriptionSelectedCustomers)
            .filter(([, selected]) => selected)
            .map(([id]) => Number(id));

        const selectedServiceIds = Object.entries(subscriptionSelectedServices)
            .filter(([, selected]) => selected)
            .map(([id]) => Number(id));

        const frequencies = subscriptionFrequencies;
        const newServicesToCreate = subscriptionNewServices;

        // 1. Crear servicios nuevos (secuencial para obtener IDs)
        const createdServices: { tempId: string; realId: number }[] = [];
        for (const newService of newServicesToCreate) {
            const name = newService.name.trim();
            if (!name) continue;
            const rawFrequency = subscriptionFrequencies[newService.tempId] ?? newService.frequency;
            const normalizedFrequency = Number.isFinite(rawFrequency) && rawFrequency > 0 ? rawFrequency : 10;
            const result = await createServiceMutation.mutateAsync({
                name,
                description: newService.description.trim(),
                defaultFrecuency: normalizedFrequency,
                inactive: false,
            });
            if (result?.id) {
                createdServices.push({ tempId: newService.tempId, realId: result.id });
            }
        }

        // 2. Combinar IDs de servicios (existentes + nuevos)
        const allServiceIds = [
            ...selectedServiceIds,
            ...createdServices.map(s => s.realId),
        ];

        // 3. Obtener lista actualizada de servicios (incluye los recién creados)
        const currentServices = createdServices.length > 0
            ? await queryClient.fetchQuery<Service[]>({ queryKey: ['services'], queryFn: fetchServices })
            : (servicesQuery.data ?? []);

        // 4. Crear suscripciones (cliente × servicio)
        for (const customerId of selectedCustomerIds) {
            for (const serviceId of allServiceIds) {
                const service = currentServices.find(s => s.id === serviceId);
                const createdService = createdServices.find(s => s.realId === serviceId);
                const freqKey = createdService?.tempId ?? serviceId;
                const rawFrequency = frequencies[freqKey] ?? service?.defaultFrequency ?? service?.defaultFrecuency ?? 10;
                const frequency = Number.isFinite(rawFrequency) && rawFrequency > 0 ? rawFrequency : 10;
                const resultId = getDefaultResultId(service);

                if (!resultId) continue;

                await addServiceMutation.mutateAsync({
                    serviceId,
                    customerId,
                    frecuency: frequency,
                    resultId,
                    startDate: new Date().toISOString(),
                    finishDate: null,
                    inactive: false,
                });
            }
        }

        // 5. Cerrar
        handleCloseSubscriptionDialog();
        toast.success('Suscripciones creadas correctamente');
    } catch {
        toast.error('Ocurrió un error al crear las suscripciones');
    } };

    const isConfirmingSubscriptions = createServiceMutation.isPending || addServiceMutation.isPending;
    const subscriptionCanConfirm =
        subscriptionSelectedCustomerCount > 0 &&
        (subscriptionSelectedServiceCount > 0 || subscriptionValidNewServicesCount > 0) &&
        subscriptionPairsCounts.newCount > 0;

    return {
        // Data
        tableData,
        filteredTableData,
        selectedClient,
        services: servicesQuery.data ?? [],
        availableCustomers: availableCustomersQuery.data ?? [],

        // Estado de carga
        servicesLoading: servicesDetailsQuery.isLoading,
        servicesError: servicesDetailsQuery.error,

        // Selección
        selectedClientId,
        expandedClientId,

        // Búsqueda de clientes
        customerSearch,
        setCustomerSearch,

        // Alta de servicios
        availableServices,
        filteredAvailableServices,
        isAddServiceOpen,
        isAddingService: addServiceMutation.isPending,
        addServiceSearch,
        setAddServiceSearch,

        // Actualización de frecuencia
        isUpdatingFrequency: updateFrequencyMutation.isPending,

        // Edición de frecuencia (diálogo)
        frequencyOverrides,
        isEditFrequencyOpen,
        editFrequencyValue,
        editService,
        editFinishDate,
        editInactive,
        setEditFrequencyValue,
        setEditFinishDate,

        // Handlers
        handleSelectClient,
        handleToggleExpand,
        handleFrequencyChange,
        handleOpenAddServiceForClient,
        handleCloseAddService,
        handleAddService,
        handleOpenEditFrequency,
        handleCloseEditFrequency,
        handleSaveFrequency,
        handleInactiveChange,

        // Diálogo unificado de suscripciones
        isSubscriptionDialogOpen,
        isConfirmingSubscriptions,
        subscriptionSelectedCustomers,
        subscriptionSelectedServices,
        subscriptionFrequencies,
        subscriptionCustomerSearch,
        subscriptionNewServices,
        subscriptionSelectedCustomerCount,
        subscriptionSelectedServiceCount,
        subscriptionValidNewServicesCount,
        subscriptionFilteredCustomers,
        subscriptionActiveServices,
        subscriptionFilteredServices,
        subscriptionServiceSearch,
        subscriptionCanConfirm,
        subscriptionNewPairsCount: subscriptionPairsCounts.newCount,
        subscriptionSkippedPairsCount: subscriptionPairsCounts.skippedCount,
        getSubscriptionDefaultFrequency,
        setSubscriptionCustomerSearch,
        setSubscriptionServiceSearch,
        handleToggleSubscriptionCustomer,
        handleToggleAllSubscriptionCustomers,
        handleToggleSubscriptionService,
        handleToggleAllSubscriptionServices,
        handleSubscriptionFrequencyChange,
        handleAddSubscriptionNewService,
        handleRemoveSubscriptionNewService,
        handleUpdateSubscriptionNewService,
        handleOpenSubscriptionDialog,
        handleCloseSubscriptionDialog,
        handleConfirmSubscriptions,
    };
};
